import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, FileText, Package } from "lucide-react";
import { productAPI, BASE_URL } from "../../services/api";

const getProductImage = (product) => {
  if (product?.image_url) {
    if (product.image_url.startsWith("http")) return product.image_url;
    return `${BASE_URL}${product.image_url}`;
  }
  return "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=300&q=80";
};

const ProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [typesMap, setTypesMap] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        productAPI.getProducts({ limit: 200 }),
        productAPI.getCategories()
      ]);
      const allProducts = prodRes.data?.data?.products || prodRes.data?.data || [];
      const allCats = (catRes.data?.data || []).filter(c =>
        allProducts.some(p => p.category_id === c.id)
      );

      const tMap = {};
      await Promise.all(
        allCats.filter(c => c.has_type).map(async c => {
          try {
            const r = await productAPI.getTypesByCategory(c.id);
            tMap[c.id] = r.data?.data || [];
          } catch { tMap[c.id] = []; }
        })
      );

      setProducts(allProducts);
      setCategories(allCats);
      setTypesMap(tMap);
      if (allCats.length > 0) {
        setActiveCategory(allCats[0].id);
        const firstTypes = tMap[allCats[0].id];
        if (firstTypes?.length > 0) setActiveType(firstTypes[0].id);
      }
    } catch (e) {
      console.error("Error fetching products:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    const types = typesMap[catId] || [];
    setActiveType(types.length > 0 ? types[0].id : null);
  };

  const activeCat = categories.find(c => c.id === activeCategory);
  const activeTypes = typesMap[activeCategory] || [];

  const filtered = products.filter(p => {
    if (p.category_id !== activeCategory) return false;
    if (activeType && p.type_id !== activeType) return false;
    return true;
  });
  const displayProducts = filtered.slice(0, 8);

  if (loading) {
    return (
      <section className="py-16 bg-[#f5f5f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 mb-8">
            {[1, 2, 3].map(i => <div key={i} className="w-28 h-10 bg-primary-100 animate-pulse rounded-full" />)}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse bg-white rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-primary-100 aspect-square" />
                <div className="p-3">
                  <div className="h-3 bg-primary-100 rounded w-3/4 mx-auto" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-[#f5f5f0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-1 bg-secondary-600 rounded-full" />
              <span className="text-xs font-bold uppercase tracking-widest text-secondary-600">Our Catalogue</span>
            </div>
            <h2 className="text-3xl font-extrabold text-primary-800 leading-tight">
              Quality Construction{" "}
              <span className="text-secondary-600">Materials</span>
            </h2>
          </div>
          <Link
            to="/products"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary-600 hover:text-secondary-700 group"
          >
            View full catalogue
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        {/* Category Tabs */}
        {categories.length > 0 && (
          <div className="flex gap-2 mb-5 flex-wrap">
            {categories.map(cat => {
              const count = products.filter(p => p.category_id === cat.id).length;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? "bg-primary-700 text-white shadow-md"
                      : "bg-white text-primary-600 border border-primary-200 hover:border-secondary-400 hover:text-secondary-600"
                  }`}
                >
                  {cat.name}
                  <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${
                    isActive ? "bg-white/20 text-white" : "bg-primary-100 text-primary-500"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Type Sub-tabs */}
        {activeTypes.length > 0 && (
          <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 border border-primary-100 w-fit shadow-sm">
            {activeTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setActiveType(type.id)}
                className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  activeType === type.id
                    ? "bg-secondary-600 text-white shadow-sm"
                    : "text-primary-500 hover:text-primary-700"
                }`}
              >
                {type.name}
              </button>
            ))}
          </div>
        )}

        {/* Products Grid */}
        {displayProducts.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {displayProducts.map(product => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="group bg-white rounded-2xl border border-primary-100 shadow-sm overflow-hidden hover:shadow-xl hover:border-secondary-300 hover:-translate-y-1 transition-all duration-200"
                >
                  <div className="relative aspect-square overflow-hidden bg-secondary-50">
                    <img
                      src={getProductImage(product)}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={e => { e.target.src = "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=200&q=80"; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-900/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <span className="inline-flex items-center gap-1 bg-secondary-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow">
                        <FileText className="w-2.5 h-2.5" /> Get Quote
                      </span>
                    </div>
                  </div>
                  <div className="px-3 py-2.5 border-t border-primary-50">
                    <p className="text-[11px] text-center font-bold text-primary-700 group-hover:text-secondary-600 transition-colors leading-snug uppercase tracking-wide line-clamp-2">
                      {product.name}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {/* View More */}
            {filtered.length > 8 && (
              <div className="flex justify-center mt-6">
                <Link
                  to={`/products?category=${activeCategory}${activeType ? `&type=${activeType}` : ''}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-white border-2 border-secondary-400 text-secondary-700 rounded-xl text-sm font-bold hover:bg-secondary-50 transition-colors shadow-sm"
                >
                  View All {activeCat?.name}
                  {activeType && activeTypes.find(t => t.id === activeType) ? ` – ${activeTypes.find(t => t.id === activeType).name}` : ''}
                  <span className="bg-secondary-100 text-secondary-700 text-[11px] font-bold px-2 py-0.5 rounded-full">
                    {filtered.length}
                  </span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 text-primary-300 bg-white rounded-2xl border border-primary-100 shadow-sm">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm text-primary-400">No products in this category</p>
          </div>
        )}

        {/* Browse All CTA */}
        <div className="flex justify-center mt-8 pt-6 border-t border-primary-100">
          <Link
            to="/products"
            className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-primary-700 text-white rounded-xl font-bold hover:bg-primary-800 transition-colors shadow-lg group"
          >
            Browse All Products
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

      </div>
    </section>
  );
};

export default ProductsSection;
